import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import CallDetailInput from './CallDetailInput';
import { CALL_DETAIL_INPUT_IDS, UI_ERROR_REFS } from './redux/constants';
import BaseSelect from '../common/BaseSelect';
import BaseInput from '../common/BaseInput';
import FilterableSelect from '../common/FilterableSelect';

class CallDetailsSection extends PureComponent {
  state = {
    inputValues: getInitialInputValues(this.props.singleOpportunity, this.props.isTeamLead),
    parsedKpiChoices: convertDataToOptions(this.props.kpis),
    parsedAgentChoices: convertAgentsToOptionData(this.props.agents),
    parsedReasonChoices: convertDataToOptions(this.props.reasons),
    parsedTeamChoices: convertTeamstoOptionData(this.props.teams),
    parsedFormTypeChoices: convertDataToOptions(this.props.formTypes),
  }

  // set up validity ref; if this section is disabled, it is valid
  inputValidMap = this.props.isDisabled
    || Object.values(CALL_DETAIL_INPUT_IDS).reduce((result, inputId) => {
      //skip team if user is a team lead.
      if(this.props.isTeamLead && inputId === CALL_DETAIL_INPUT_IDS.team) {
        return result;
      }
      
      //call log ID not required, so always valid
      inputId === CALL_DETAIL_INPUT_IDS.callLogId ? result[inputId] = true : result[inputId] = false;

      return result;
    }, {})

  /**
   * Handles receiving call details; we need to parse datetime string
   */
  componentDidUpdate(prevProps, prevState) {
    const callDate = this.props.callDetails && this.props.callDetails.callDate;

    // we can't compare to the inputValues.dateOfCall because that date is parsed
    // and we do not want to parse every update, so we use the ref
    if (callDate && callDate !== this.dateRef) {
      this.handleEvent(callDate, CALL_DETAIL_INPUT_IDS.dateOfCall, Boolean(callDate));
    }

    if (JSON.stringify(prevProps.agents) !== JSON.stringify(this.props.agents)) {
      this.setState({ parsedAgentChoices: convertAgentsToOptionData(this.props.agents) });
    }

    if (JSON.stringify(prevProps.kpis) !== JSON.stringify(this.props.kpis)) {
      this.setState({ parsedKpiChoices: convertDataToOptions(this.props.kpis) });
    }

    if (JSON.stringify(prevProps.reasons) !== JSON.stringify(this.props.reasons)) {
      this.setState({ parsedReasonChoices: convertDataToOptions(this.props.reasons) });
    }

    if (JSON.stringify(prevProps.formTypes) !== JSON.stringify(this.props.formTypes)) {
      this.setState({ parsedFormTypeChoices: convertDataToOptions(this.props.formTypes) });
    }

    if (JSON.stringify(prevProps.teams) !== JSON.stringify(this.props.teams)) {
      this.setState({ parsedTeamChoices: convertTeamstoOptionData(this.props.teams) });
    }
  }

  /**
   * Override of basic handleEvent, calls fetchAgents to update on change
   * @param {string} value
   * @param {number} inputId
   * @param {boolean} isValid
   */
  handleTeamEvent = (value, inputId, isValid) => {
    this.handleEvent(value, inputId, isValid);
  }

  handleFormTypeChange = (value, inputId, isValid) => {
    this.props.handleFormTypeChange(value);
    this.handleEvent(value, inputId, isValid);
  }

  /**
   * handles input events, sets state with inputValues, notifies parent w/ values and validity
   * @param {string} value
   * @param {number} inputId
   * @param {boolean} isValid
   */
  handleEvent = (value, inputId, isValid) => {
    const { eventHandler, sectionId } = this.props;

    const isDateChange = inputId === CALL_DETAIL_INPUT_IDS.dateOfCall; 
    const isCallLogChange = inputId === CALL_DETAIL_INPUT_IDS.callLogId;
    const isTeamChange = inputId === CALL_DETAIL_INPUT_IDS.team;
    const isFormTypeChange = inputId === CALL_DETAIL_INPUT_IDS.formType;
    // we must keep a date ref for the componentDidUpdate logic to be efficient
    this.dateRef = isDateChange ? value : this.dateRef;

    const parsedValue = value;

    // update values
    this.setState((state) => {
      return {
        inputValues: {
          ...state.inputValues,
          [inputId]: parsedValue,
          // we erase call date if user changes call log id
          ...isCallLogChange && { [CALL_DETAIL_INPUT_IDS.dateOfCall]: '' },
          // erase agent if user changes team
          ...isTeamChange && { [CALL_DETAIL_INPUT_IDS.salesAgent] : ''},
          // erase date of call and call log id if user changes form type
          ...isFormTypeChange && { [CALL_DETAIL_INPUT_IDS.dateOfCall]: '', [CALL_DETAIL_INPUT_IDS.callLogId]: ''},
        }
      };
    });
    // update validity
    this.inputValidMap = {
      ...this.inputValidMap,
      [inputId]: isCallLogChange ? true : isValid,
      ...isFormTypeChange && {
        [CALL_DETAIL_INPUT_IDS.dateOfCall]: parsedValue === 3, //set date of call true only if form type changes to process flow evaluation
      }
    };

    // short-circuit, if this field is not valid, no use checking the rest
    const areAllFieldsValid = (isCallLogChange || isValid) && Object.values(this.inputValidMap).every((val) => val);
    const postifiedValue = postifyValue(parsedValue, inputId);

    // update main form
    eventHandler(postifiedValue, sectionId, areAllFieldsValid);
  }

  handleFetchCallDetails = (callLogId) => {
    // we must clear old date before fetching new one
    this.handleEvent('', CALL_DETAIL_INPUT_IDS.dateOfCall, false);
    this.props.fetchCallDetails({ callLogId });
  }
    
  render() {
    const { isTeamLead, isDisabled, uiError, isErrorTriggered } = this.props;
    const {
      inputValues,
      parsedKpiChoices,
      parsedTeamChoices,
      parsedAgentChoices,
      parsedReasonChoices,
      parsedFormTypeChoices,
    } = this.state;

    const isCallLogIdError = isErrorTriggered
      || Boolean(uiError && uiError.errorRef === UI_ERROR_REFS.callLogIdError);

    const commonProps = {
      isDisabled,
      isErrorTriggered,
      eventHandler: this.handleEvent,
    }

    const teamProps = {
      isDisabled,
      isErrorTriggered,
      eventHandler: this.handleTeamEvent,
    }

    const agentProps = {
      isErrorTriggered,
      eventHandler: this.handleEvent,
      isDisabled: (!isTeamLead && !inputValues[CALL_DETAIL_INPUT_IDS.team]) || isDisabled
    }

    const callLogProps = {
      ...commonProps,
      isDisabled: (inputValues[CALL_DETAIL_INPUT_IDS.formType] === 3) || isDisabled
    }

    const dateProps = {
      ...commonProps,
      isRequired: inputValues[CALL_DETAIL_INPUT_IDS.formType] !== 3,
      isDisabled: (inputValues[CALL_DETAIL_INPUT_IDS.formType] === 3) || isDisabled,
      additionalProps: { formType: inputValues[CALL_DETAIL_INPUT_IDS.formType] },
    }

    const formTypeProps = {
      isDisabled,
      isErrorTriggered,
      eventHandler: this.handleFormTypeChange,
    }
      
    return (
      <div className="ac-call-details">
        <div className="ac-call-details__left-wrap">
          {!isTeamLead && (
            <CallDetailInput
              {...teamProps}
              Input={BaseSelect}
              labelText="Team"
              optionData={parsedTeamChoices}
              inputKey={CALL_DETAIL_INPUT_IDS.team}
              value={inputValues[CALL_DETAIL_INPUT_IDS.team]}
            />
          )}
          <CallDetailInput
            {...agentProps}
            Input={FilterableSelect}
            labelText={isTeamLead ? 'Agent' : 'Agent/Member'}
            optionData={parsedAgentChoices}
            inputKey={CALL_DETAIL_INPUT_IDS.salesAgent}
            value={inputValues[CALL_DETAIL_INPUT_IDS.salesAgent]}
          />
          <CallDetailInput
            {...callLogProps}
            isRequired={false}
            Input={BaseInput}
            labelText="Call Log ID"
            isErrorTriggered={isCallLogIdError}
            inputKey={CALL_DETAIL_INPUT_IDS.callLogId}
            customErrorMessage="Call Log ID not found."
            value={inputValues[CALL_DETAIL_INPUT_IDS.callLogId]}
            additionalProps={{
              hasInnerButton: true,
              stripAlphaChars: true,
              innerButtonCallback: this.handleFetchCallDetails,
              innerButtonClassNames: 'shared__flex-cen-cen  ac-call-details__id-lookup',
            }}
          />
          <CallDetailInput
            {...dateProps}
            Input={BaseInput}
            labelText="Date of Call/Incident"
            inputKey={CALL_DETAIL_INPUT_IDS.dateOfCall}
            value={inputValues[CALL_DETAIL_INPUT_IDS.dateOfCall]}
          />
        </div>
        <div className="ac-call-details__right-wrap">
          <CallDetailInput
            {...commonProps}
            Input={BaseSelect}
            labelText="WHAT KPI is being coached?"
            optionData={parsedKpiChoices}
            inputKey={CALL_DETAIL_INPUT_IDS.targetKpi}
            value={inputValues[CALL_DETAIL_INPUT_IDS.targetKpi]}
          />
          <CallDetailInput
            {...commonProps}
            Input={BaseInput}
            labelText="Target KPI pre-Coaching"
            inputKey={CALL_DETAIL_INPUT_IDS.kpiPerformance}
            value={inputValues[CALL_DETAIL_INPUT_IDS.kpiPerformance]}
            />
          <CallDetailInput
            {...commonProps}
            Input={BaseSelect}
            labelText="Coaching Category"
            optionData={parsedReasonChoices}
            inputKey={CALL_DETAIL_INPUT_IDS.reason}
            value={inputValues[CALL_DETAIL_INPUT_IDS.reason]}
          />
          <CallDetailInput
            {...formTypeProps}
            Input={BaseSelect}
            labelText="Form Type"
            optionData={parsedFormTypeChoices}
            inputKey={CALL_DETAIL_INPUT_IDS.formType}
            value={inputValues[CALL_DETAIL_INPUT_IDS.formType]}
          />
        </div>
      </div>
    );
  }
}

CallDetailsSection.propTypes = {
  uiError: PropTypes.object,
  kpis: PropTypes.array.isRequired,
  teams: PropTypes.array.isRequired,
  agents: PropTypes.array.isRequired,
  reasons: PropTypes.array.isRequired,
  formTypes: PropTypes.array.isRequired,
  singleOpportunity: PropTypes.object,
  isDisabled: PropTypes.bool.isRequired,
  sectionId: PropTypes.string.isRequired,
  eventHandler: PropTypes.func.isRequired,
  fetchCallDetails: PropTypes.func.isRequired,
  isErrorTriggered: PropTypes.bool.isRequired,
};

CallDetailsSection.defaultProps = {
  uiError: null,
  singleOpportunity: null,
};

// === helpers ===
/**
 * Sets up input values to be that of an existing opportunity or all set to empty value
 * @param {?object} singleOpportunity - a fetch existing opportunity (null if this is a new)
 */
function getInitialInputValues(singleOpportunity, isTeamLead) {
  return Object.values(CALL_DETAIL_INPUT_IDS).reduce((result, inputId) => {
    if (singleOpportunity) {
      // some of the data is nested... so we key into each section appropriately
      let value;
      switch (inputId) {
        case CALL_DETAIL_INPUT_IDS.team:
          const isTeamLead = singleOpportunity.salesAgent.agentPosition === 2;
          value = isTeamLead ? singleOpportunity.salesAgent.teamLeadTeams[0].id : singleOpportunity.salesAgent.teamId;
          break;
        case CALL_DETAIL_INPUT_IDS.salesAgent:
          value = singleOpportunity.salesAgent.id;
          break;
        case CALL_DETAIL_INPUT_IDS.targetKpi:
          value = singleOpportunity.targetKpi.id;
          break;
        case CALL_DETAIL_INPUT_IDS.reason:
          value = singleOpportunity.reason.id;
          break;
        case CALL_DETAIL_INPUT_IDS.formType:
          value = singleOpportunity.coachingFormType.id;
          break;
        case CALL_DETAIL_INPUT_IDS.dateOfCall:
          value = singleOpportunity.dateOfCall ? parseCallDate(singleOpportunity.dateOfCall) : '';
          break;
        default:
          value = singleOpportunity[inputId];
          break;
      }

      if ((isTeamLead && inputId !== CALL_DETAIL_INPUT_IDS.team) || !isTeamLead) result[inputId] = value;
      return result;
    }

    if ((isTeamLead && inputId !== CALL_DETAIL_INPUT_IDS.team) || !isTeamLead) result[inputId] = '';
    return result;
  }, {});
}

/**
 * Converts array of strings into option data a BaseSelect component can accept
 * @param {array} agents
 */
function convertAgentsToOptionData(agents) {
  return agents.map(({ id, fullName }, i) => {
    return {
      value: id,
      key: i,
      text: fullName,
    };
  });
}

function convertTeamstoOptionData(teams) {
  return teams.map(({ id, teamName }, i) => {
    return { 
      value: id,
      key: i,
      text: teamName,
    }
  });
}

/**
 * Converts array of strings into option data a BaseSelect component can accept
 * @param {array} data
 */
function convertDataToOptions(data) {
  return data.map(({ id, name }, i) => {
    return {
      value: id,
      key: i,
      text: name,
    };
  });
}

/**
 * Remember all sections are responsible for sending data to main form in a way that matches req model.
 * Some values in the form POST are nested one level deep with "id", some parsed as numbers, some strings
 * @param {*} value
 * @param {string} inputId
 */
function postifyValue(value, inputId) {
  const requiresParseInt = inputId === CALL_DETAIL_INPUT_IDS.salesAgent
    || inputId === CALL_DETAIL_INPUT_IDS.callLogId || inputId === CALL_DETAIL_INPUT_IDS.targetKpi
    || inputId === CALL_DETAIL_INPUT_IDS.reason || inputId === CALL_DETAIL_INPUT_IDS.formType;

  const requiresId = inputId === CALL_DETAIL_INPUT_IDS.salesAgent
    || inputId === CALL_DETAIL_INPUT_IDS.targetKpi || inputId === CALL_DETAIL_INPUT_IDS.reason
    || inputId === CALL_DETAIL_INPUT_IDS.formType;

  const requiresDateParse = inputId === CALL_DETAIL_INPUT_IDS.dateOfCall;

  let parsedValue = value;
  if (requiresParseInt) parsedValue = parseInt(value, 10);
  if (requiresDateParse) parsedValue = unparseCallDate(value);

  return requiresId ? { [inputId]: { id: parsedValue } } : { [inputId]: parsedValue };
}

/**
 * Parse json date string into mm/dd/yyyy format
 * @param {string} callDate - comes in this pattern: "2020-11-24T23:24:21.930Z"
 */
export function parseCallDate(callDate) {
  const [yyyy, mm, dd] = callDate.split('T')[0].split('-');

  return `${mm}/${dd}/${yyyy}`;
}

/**
 * Parse json date string into yyyy-mm-dd from mm/dd/yyyy format
 * @param {string} callDate - mm/dd/yyyy
 */
function unparseCallDate(callDate) {
  const [mm, dd, yyyy] = callDate.split('/');

  return `${yyyy}-${mm}-${dd}`;
}

export default CallDetailsSection;



// WEBPACK FOOTER //
// ./src/features/agent-coaching/CallDetailsSection.js